import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Table, Badge, Alert, Spinner } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import 'bootstrap/dist/css/bootstrap.min.css';

// ✅ IMPORTAR EL NUEVO SISTEMA DE SEGURIDAD
import { apiGet, estaAutenticado, obtenerUsuarioActual } from '../utils/apiConfig';

function ReportsPage() {
  const [sidebarAbierto, setSidebarAbierto] = useState(true);
  const [sinPermisos, setSinPermisos] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [reporteActivo, setReporteActivo] = useState('general');
  const [error, setError] = useState('');
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    fechaInicio: '',
    fechaFin: '',
    tipoDocumento: 'todos',
    estado: 'todos',
    usuario: 'todos'
  });

  // Estados para datos de reportes
  const [datosReporte, setDatosReporte] = useState({
    resumenGeneral: {},
    documentos: [],
    usuarios: []
  });

  // ✅ VERIFICAR AUTENTICACIÓN Y PERMISOS AL CARGAR
  useEffect(() => {
    // 1. Verificar que esté autenticado
    if (!estaAutenticado()) {
      alert('🔐 Debes iniciar sesión para acceder a los reportes');
      window.location.href = '/login';
      return;
    }

    // 2. Verificar permisos específicos
    const usuario = obtenerUsuarioActual();
    if (!usuario || !usuario.permisos.verReportes) {
      console.log('🚫 Usuario sin permisos para reportes:', usuario);
      setSinPermisos(true);
      return;
    }
    
    console.log('✅ Usuario autorizado para reportes:', usuario.correo);
    
    // 3. Cargar datos iniciales
    cargarDatosIniciales();
  }, []);

  const cargarDatosIniciales = async () => {
    try {
      setCargando(true);
      setError('');
      
      console.log('📊 Cargando datos iniciales de reportes...');

      // ✅ USAR apiGet EN LUGAR DE AXIOS DIRECTO
      try {
        // Cargar usuarios para filtros
        const usuarios = await apiGet('/api/users');
        setDatosReporte(prev => ({
          ...prev,
          usuarios: usuarios
        }));
        console.log('👥 Usuarios cargados:', usuarios.length);
      } catch (error) {
        console.warn('⚠️ Error cargando usuarios:', error.message);
        // No es crítico, continuar sin usuarios en filtros
      }

      // Cargar reporte general por defecto
      await generarReporte();
      
    } catch (error) {
      console.error('❌ Error cargando datos iniciales:', error);
      setError('Error al cargar datos iniciales. Por favor, recarga la página.');
    } finally {
      setCargando(false);
    }
  };

  const generarReporte = async () => {
    try {
      setCargando(true);
      setError('');

      let endpoint = '';
      let params = {};

      switch (reporteActivo) {
        case 'general':
          endpoint = '/api/reportes/general';
          break;
        case 'fechas':
          endpoint = '/api/reportes/por-fechas';
          params = {
            fechaInicio: filtros.fechaInicio,
            fechaFin: filtros.fechaFin,
            tipo: filtros.tipoDocumento !== 'todos' ? filtros.tipoDocumento : undefined,
            estado: filtros.estado !== 'todos' ? filtros.estado : undefined
          };
          break;
        case 'rendimiento':
          endpoint = '/api/reportes/rendimiento';
          params = {
            usuario: filtros.usuario !== 'todos' ? filtros.usuario : undefined
          };
          break;
        default:
          throw new Error('Tipo de reporte no válido');
      }

      console.log(`📈 Generando reporte ${reporteActivo} con params:`, params);

      // ✅ USAR apiGet CON PARÁMETROS
      const response = await apiGet(endpoint, { params });
      
      setDatosReporte(prev => ({
        ...prev,
        resumenGeneral: response.resumen || {},
        documentos: response.documentos || []
      }));

      console.log('📊 Reporte generado exitosamente:', {
        tipo: reporteActivo,
        documentos: response.documentos?.length || 0,
        resumen: response.resumen
      });

    } catch (error) {
      console.error('❌ Error generando reporte:', error);
      
      // Manejar diferentes tipos de errores
      if (error.message.includes('Sesión expirada')) {
        setError('Tu sesión ha expirado. Serás redirigido al login.');
        setTimeout(() => window.location.href = '/login', 2000);
      } else if (error.message.includes('Permisos insuficientes')) {
        setError('No tienes permisos para generar este reporte.');
        setSinPermisos(true);
      } else if (error.message.includes('Rate limit')) {
        setError('Has realizado demasiadas consultas. Espera un momento e intenta nuevamente.');
      } else {
        setError('Error al generar el reporte. Por favor, intenta nuevamente.');
      }
    } finally {
      setCargando(false);
    }
  };

  const exportarExcel = async () => {
    try {
      setCargando(true);
      setError('');
      
      const params = {
        tipo: reporteActivo,
        ...filtros
      };

      console.log('📥 Exportando Excel con params:', params);

      // ✅ USAR apiGet PARA EXPORTACIÓN
      const response = await apiGet('/api/reportes/exportar-excel', {
        params,
        responseType: 'blob'
      });

      // Crear y descargar archivo
      const blob = new Blob([response], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `reporte_${reporteActivo}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      console.log('✅ Excel exportado exitosamente');

    } catch (error) {
      console.error('❌ Error exportando Excel:', error);
      
      if (error.message.includes('Rate limit')) {
        setError('Has realizado demasiadas exportaciones. Espera un momento e intenta nuevamente.');
      } else {
        setError('Error al exportar el reporte. Por favor, intenta nuevamente.');
      }
    } finally {
      setCargando(false);
    }
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const obtenerBadgeEstado = (estado) => {
    const badges = {
      'pendiente': 'warning',
      'firmado': 'info',
      'finalizado': 'success',
      'rechazado': 'danger'
    };
    return badges[estado] || 'secondary';
  };

  const obtenerIconoTipo = (tipo) => {
    return tipo === 'medico' ? '🏥' : '📋';
  };

  // ✅ PANTALLA DE SIN PERMISOS MEJORADA
  if (sinPermisos) {
    return (
      <div className="d-flex">
        <Sidebar onToggle={setSidebarAbierto} />
        <div
          className="flex-grow-1 d-flex align-items-center justify-content-center"
          style={{ 
            marginLeft: sidebarAbierto ? '240px' : '80px',
            transition: 'margin-left 0.3s',
            minHeight: '100vh',
            backgroundColor: '#f8f9fa'
          }}
        >
          <Alert variant="warning" className="text-center" style={{ maxWidth: '500px' }}>
            <Alert.Heading>🚫 Acceso Denegado</Alert.Heading>
            <p>No tienes permisos para ver los reportes del sistema.</p>
            <hr />
            <p className="mb-0">
              Contacta al administrador para obtener el permiso <strong>"verReportes"</strong>.
            </p>
            <Button 
              variant="outline-primary" 
              className="mt-3"
              onClick={() => window.location.href = '/dashboard'}
            >
              🔙 Volver al Dashboard
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex">
      <Sidebar onToggle={setSidebarAbierto} />
      
      <div
        className="flex-grow-1"
        style={{ 
          marginLeft: sidebarAbierto ? '240px' : '80px',
          transition: 'margin-left 0.3s',
          padding: sidebarAbierto ? '20px 30px' : '20px 15px',
          minHeight: '100vh',
          backgroundColor: '#f8f9fa'
        }}
      >
        {/* HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">📊 Centro de Reportes</h2>
            <p className="text-muted mb-0">Generación y exportación de reportes avanzados</p>
          </div>
          <div>
            <Button 
              variant="success" 
              onClick={exportarExcel}
              disabled={cargando}
              className="me-2"
            >
              {cargando ? (
                <Spinner as="span" animation="border" size="sm" className="me-2" />
              ) : (
                '📥 '
              )}
              Exportar Excel
            </Button>
            <Button 
              variant="primary" 
              onClick={generarReporte}
              disabled={cargando}
            >
              {cargando ? (
                <Spinner as="span" animation="border" size="sm" className="me-2" />
              ) : (
                '🔄 '
              )}
              Generar Reporte
            </Button>
          </div>
        </div>

        {/* ✅ MOSTRAR ERRORES */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            <Alert.Heading>❌ Error</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        )}

        {/* SELECTOR DE TIPO DE REPORTE */}
        <Row className="mb-4">
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white">
                <h5 className="mb-0">🎯 Tipo de Reporte</h5>
              </Card.Header>
              <Card.Body>
                <div className="d-flex gap-3">
                  <Button
                    variant={reporteActivo === 'general' ? 'primary' : 'outline-primary'}
                    onClick={() => setReporteActivo('general')}
                    disabled={cargando}
                  >
                    📈 Reporte General
                  </Button>
                  <Button
                    variant={reporteActivo === 'fechas' ? 'primary' : 'outline-primary'}
                    onClick={() => setReporteActivo('fechas')}
                    disabled={cargando}
                  >
                    📅 Por Fechas
                  </Button>
                  <Button
                    variant={reporteActivo === 'rendimiento' ? 'primary' : 'outline-primary'}
                    onClick={() => setReporteActivo('rendimiento')}
                    disabled={cargando}
                  >
                    ⚡ Rendimiento
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* FILTROS DINÁMICOS */}
        {(reporteActivo === 'fechas' || reporteActivo === 'rendimiento') && (
          <Row className="mb-4">
            <Col>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white">
                  <h5 className="mb-0">🔍 Filtros</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    {reporteActivo === 'fechas' && (
                      <>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Fecha Inicio</Form.Label>
                            <Form.Control
                              type="date"
                              value={filtros.fechaInicio}
                              onChange={(e) => handleFiltroChange('fechaInicio', e.target.value)}
                              disabled={cargando}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Fecha Fin</Form.Label>
                            <Form.Control
                              type="date"
                              value={filtros.fechaFin}
                              onChange={(e) => handleFiltroChange('fechaFin', e.target.value)}
                              disabled={cargando}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Tipo Documento</Form.Label>
                            <Form.Select
                              value={filtros.tipoDocumento}
                              onChange={(e) => handleFiltroChange('tipoDocumento', e.target.value)}
                              disabled={cargando}
                            >
                              <option value="todos">Todos los tipos</option>
                              <option value="medico">🏥 Médicos</option>
                              <option value="administrativo">📋 Administrativos</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Estado</Form.Label>
                            <Form.Select
                              value={filtros.estado}
                              onChange={(e) => handleFiltroChange('estado', e.target.value)}
                              disabled={cargando}
                            >
                              <option value="todos">Todos los estados</option>
                              <option value="pendiente">⏳ Pendientes</option>
                              <option value="finalizado">✅ Finalizados</option>
                              <option value="rechazado">❌ Rechazados</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </>
                    )}

                    {reporteActivo === 'rendimiento' && (
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Usuario</Form.Label>
                          <Form.Select
                            value={filtros.usuario}
                            onChange={(e) => handleFiltroChange('usuario', e.target.value)}
                            disabled={cargando}
                          >
                            <option value="todos">Todos los usuarios</option>
                            {datosReporte.usuarios.map((usuario) => (
                              <option key={usuario._id} value={usuario._id}>
                                {usuario.nombres}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* RESUMEN EJECUTIVO */}
        {datosReporte.resumenGeneral && Object.keys(datosReporte.resumenGeneral).length > 0 && (
          <Row className="mb-4">
            <Col>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white">
                  <h5 className="mb-0">📋 Resumen Ejecutivo</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3} className="text-center">
                      <div className="display-6 text-primary">📄</div>
                      <h4 className="text-primary">{datosReporte.resumenGeneral.total || 0}</h4>
                      <p className="text-muted mb-0">Total Documentos</p>
                    </Col>
                    <Col md={3} className="text-center">
                      <div className="display-6 text-success">✅</div>
                      <h4 className="text-success">{datosReporte.resumenGeneral.finalizados || 0}</h4>
                      <p className="text-muted mb-0">Finalizados</p>
                    </Col>
                    <Col md={3} className="text-center">
                      <div className="display-6 text-warning">⏳</div>
                      <h4 className="text-warning">{datosReporte.resumenGeneral.pendientes || 0}</h4>
                      <p className="text-muted mb-0">Pendientes</p>
                    </Col>
                    <Col md={3} className="text-center">
                      <div className="display-6 text-danger">❌</div>
                      <h4 className="text-danger">{datosReporte.resumenGeneral.rechazados || 0}</h4>
                      <p className="text-muted mb-0">Rechazados</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* TABLA DE DOCUMENTOS */}
        <Row>
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white">
                <h5 className="mb-0">📑 Detalle de Documentos</h5>
              </Card.Header>
              <Card.Body className="p-0">
                {cargando ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Generando reporte...</p>
                  </div>
                ) : datosReporte.documentos && datosReporte.documentos.length > 0 ? (
                  <Table responsive hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Tipo</th>
                        <th>Fecha</th>
                        <th>Cédula</th>
                        <th>Paciente</th>
                        <th>Documento</th>
                        <th>Estado</th>
                        <th>Enviado por</th>
                        {reporteActivo === 'rendimiento' && <th>Tiempo Respuesta</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {datosReporte.documentos.map((doc) => (
                        <tr key={doc._id}>
                          <td>
                            <span className="fs-5">{obtenerIconoTipo(doc.tipo)}</span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {formatearFecha(doc.createdAt)}
                            </small>
                          </td>
                          <td>
                            <code className="text-primary">{doc.cedula}</code>
                          </td>
                          <td>{doc.nombres}</td>
                          <td>
                            <small>{doc.nombreDocumento}</small>
                          </td>
                          <td>
                            <Badge bg={obtenerBadgeEstado(doc.estado)} className="text-capitalize">
                              {doc.estado}
                            </Badge>
                          </td>
                          <td>
                            <small className="text-muted">
                              {doc.enviadoPor || 'No identificado'}
                            </small>
                          </td>
                          {reporteActivo === 'rendimiento' && (
                            <td>
                              <small className="text-muted">
                                {doc.tiempoRespuesta || 'N/A'}
                              </small>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted mb-0">📭 No hay datos para mostrar con los filtros seleccionados</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default ReportsPage;